import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, computed, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';

import { LoaderDots } from '../loader-dots';

import { Icon, IconType } from 'app/shared/icon';

export enum ButtonType {
    primary = 'primary',
    secondary = 'secondary',
    text = 'text',
    inline = 'inline',
}

@Component({
    selector: 'stby-button',
    styleUrl: './button.scss',
    templateUrl: './button.html',
    imports: [MatIconModule, TranslatePipe, Icon, NgClass, NgTemplateOutlet, RouterLink, LoaderDots],
})
export class Button {
    readonly class = input<string>();
    readonly label = input<string>();
    readonly icon = input<IconType>();
    readonly iconSize = input<string>();
    readonly rotate = input<number>(0);

    readonly type = input<ButtonType | keyof typeof ButtonType>(ButtonType.primary);
    readonly iconPosition = input<'right' | 'left'>('right');
    readonly loading = input<boolean>(false);
    readonly disabled = input<boolean>(false);
    readonly submit = input<boolean, any>(false, { transform: coerceBooleanProperty });
    readonly destructive = input<boolean, any>(false, { transform: coerceBooleanProperty });

    readonly href = input<string>();
    readonly routeTo = input<string | any[]>();
    readonly fragment = input<string>();
    readonly queryParams = input<{
        [key: string]: any;
    }>({});
    readonly labelParams = input<Record<string, any>>({});

    readonly id = input<string>('');

    size = input<'regular' | 'small'>('regular');

    iconSize$ = computed(() => {
        const iconSize = this.iconSize();
        if (iconSize) {
            return iconSize;
        }
        return this.size() === 'small' ? 12 : 14;
    });

    btnClass = computed(() => {
        return ['btn', 'btn--' + this.type(), 'btn--' + this.size()];
    });

    hasUnderline = computed(() => {
        return this.type() === 'text' || this.type() === 'inline';
    });

    readonly clicked = output<void>();

    handleClick(event: Event): void {
        if (this.disabled() || this.loading()) {
            return;
        }

        if (!this.submit()) {
            if (event) {
                event.stopPropagation();
            }

            this.clicked.emit();
        } else {
            if (event) {
                event.preventDefault();
            }

            const button = event.target as HTMLButtonElement;
            const form = button.closest('form');
            if (form) {
                form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
            }
        }
    }
}
